/**
 * The media composer's image editor, as WhatsApp Web's (measured in "Apenas Leo" on 2026-10-29):
 * crop and rotate, six filters, drawing, text, shapes, blur, emoji and stickers, undo and redo,
 * and the photo's quality. This file holds the edit and its rules; imageRender.ts draws it.
 *
 * Positions and sizes are in the source photo's pixels, before the turn and the crop, so a crop or
 * a turn made after a drawing keeps the drawing on the same spot of the photo.
 */

export type Rotation = 0 | 90 | 180 | 270;
export interface Point {
  x: number;
  y: number;
}
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const FILTERS = ["none", "pop", "bw", "cool", "chrome", "film"] as const;
export type FilterId = (typeof FILTERS)[number];

/**
 * 3 × 4 colour matrices (r, g, b, 1 → each channel, on 0–255) fitted to what WhatsApp Web's
 * filters made of a test card of known colours; within 9 levels of it (Chrome within 21).
 */
export const FILTER_MATRIX: Record<Exclude<FilterId, "none">, number[][]> = {
  pop: [
    [0.993, -0.126, 0.028, 31.4],
    [0.001, 1.516, -0.007, -78],
    [-0.002, -0.004, 1.44, -60.4],
  ],
  bw: [
    [0.305, 0.473, 0.125, 31.9],
    [0.305, 0.473, 0.125, 31.9],
    [0.305, 0.473, 0.125, 31.9],
  ],
  cool: [
    [0.703, 0.205, 0.009, -14.5],
    [0.062, 1.128, 0.021, -66.6],
    [0.079, 0.228, 0.907, -40.3],
  ],
  chrome: [
    [0.957, -0.147, 0.049, 41.1],
    [-0.09, 1.07, -0.153, 55.6],
    [0.001, -0.002, 1.102, 38.8],
  ],
  film: [
    [0.993, -0.126, 0.028, 31.4],
    [-0.001, 1.238, -0.011, -16.3],
    [0.004, 0, 0.764, 88.2],
  ],
};

/** Applies a filter to RGBA pixels in place; alpha is kept. */
export function applyFilter(data: Uint8ClampedArray, filter: FilterId): void {
  if (filter === "none") return;
  const m = FILTER_MATRIX[filter];
  const [r0, g0, b0] = m as [number[], number[], number[]];
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]!,
      g = data[i + 1]!,
      b = data[i + 2]!;
    data[i] = r0[0]! * r + r0[1]! * g + r0[2]! * b + r0[3]!;
    data[i + 1] = g0[0]! * r + g0[1]! * g + g0[2]! * b + g0[3]!;
    data[i + 2] = b0[0]! * r + b0[1]! * g + b0[2]! * b + b0[3]!;
  }
}

/** The palette of drawing, text and shapes, in WhatsApp's order. */
export const COLORS = [
  { id: "black", hex: "#434343" },
  { id: "gray", hex: "#9da0a9" },
  { id: "white", hex: "#ffffff" },
  { id: "blue", hex: "#33ceff" },
  { id: "green", hex: "#64dc2f" },
  { id: "purple", hex: "#bd73ff" },
  { id: "orange", hex: "#f49226" },
  { id: "red", hex: "#ff4a4a" },
] as const;
export const DRAW_COLOR = "#33ceff";
export const INK = "#434343";
/** The four brush sizes of drawing, as screen pixels. */
export const BRUSH_SIZES = [3, 6, 12, 20] as const;

export const FONTS = [
  { id: "sans", label: "Sans Serif", family: "sans-serif", weight: 400 },
  { id: "serif", label: "Serif", family: "'Noto Serif', serif", weight: 400 },
  { id: "norican", label: "Norican", family: "Norican, cursive", weight: 400 },
  // WhatsApp's Bryndan Write has no free licence; Gochi Hand is the closest open hand.
  {
    id: "hand",
    label: "Gochi Hand",
    family: "'Gochi Hand', cursive",
    weight: 400,
  },
  { id: "oswald", label: "Oswald", family: "Oswald, sans-serif", weight: 700 },
] as const;
export type FontId = (typeof FONTS)[number]["id"];
export type Align = "left" | "center" | "right";
/** Text size on screen when it is placed, as WhatsApp's. */
export const TEXT_SIZE = 17;

export type ShapeKind = "rect" | "circle" | "line" | "arrow";
export const SHAPE_WIDTH = { min: 1, max: 25, default: 3 } as const;
export const BLUR_SIZE = { min: 10, max: 100, default: 50 } as const;
export type BlurMode = "pixelate" | "blur";

interface Placed {
  id: string;
  /** Centre, in source pixels. */
  x: number;
  y: number;
  /** Unturned box, in source pixels. */
  w: number;
  h: number;
  /** Degrees, relative to the source photo. */
  angle: number;
}
export interface TextItem extends Placed {
  kind: "text";
  text: string;
  color: string;
  font: FontId;
  align: Align;
  background: boolean;
  size: number;
}
export interface ShapeItem extends Placed {
  kind: "shape";
  shape: ShapeKind;
  stroke: string | null;
  fill: string | null;
  width: number;
}
export interface EmojiItem extends Placed {
  kind: "emoji";
  emoji: string;
}
export interface StickerItem extends Placed {
  kind: "sticker";
  url: string;
}
export type Item = TextItem | ShapeItem | EmojiItem | StickerItem;

export interface DrawMark {
  kind: "draw";
  color: string;
  size: number;
  points: number[];
}
export interface BlurMark {
  kind: "blur";
  mode: BlurMode;
  size: number;
  points: number[];
}
export type Mark = DrawMark | BlurMark;

export interface EditState {
  rotation: Rotation;
  /** In the turned photo's pixels; null is the whole photo. */
  crop: Rect | null;
  filter: FilterId;
  marks: Mark[];
  items: Item[];
}

export function blankEdit(): EditState {
  return { rotation: 0, crop: null, filter: "none", marks: [], items: [] };
}

/** Nothing changed: the photo goes as it came (or only resized for the quality). */
export function isPristine(s: EditState): boolean {
  return (
    s.rotation === 0 &&
    s.crop === null &&
    s.filter === "none" &&
    !s.marks.length &&
    !s.items.length
  );
}

export function cloneEdit(s: EditState): EditState {
  return JSON.parse(JSON.stringify(s)) as EditState;
}

// ── history ────────────────────────────────────────────────────────────────────────────────────

export interface History {
  past: EditState[];
  future: EditState[];
}
export const blankHistory = (): History => ({ past: [], future: [] });

/** Records the state before a change; a new change drops what had been undone. */
export function remember(h: History, before: EditState): History {
  return { past: [...h.past, cloneEdit(before)].slice(-100), future: [] };
}
export function undo(
  h: History,
  current: EditState,
): { state: EditState; history: History } | null {
  const previous = h.past.at(-1);
  if (!previous) return null;
  return {
    state: cloneEdit(previous),
    history: {
      past: h.past.slice(0, -1),
      future: [cloneEdit(current), ...h.future],
    },
  };
}
export function redo(
  h: History,
  current: EditState,
): { state: EditState; history: History } | null {
  const next = h.future[0];
  if (!next) return null;
  return {
    state: cloneEdit(next),
    history: {
      past: [...h.past, cloneEdit(current)],
      future: h.future.slice(1),
    },
  };
}

// ── geometry ───────────────────────────────────────────────────────────────────────────────────

export function turnedSize(
  w: number,
  h: number,
  r: Rotation,
): { w: number; h: number } {
  return r === 90 || r === 270 ? { w: h, h: w } : { w, h };
}

/** A source point in the turned photo. */
export function toTurned(p: Point, w: number, h: number, r: Rotation): Point {
  if (r === 90) return { x: h - p.y, y: p.x };
  if (r === 180) return { x: w - p.x, y: h - p.y };
  if (r === 270) return { x: p.y, y: w - p.x };
  return { x: p.x, y: p.y };
}

/** A point of the turned photo back in the source. */
export function fromTurned(p: Point, w: number, h: number, r: Rotation): Point {
  if (r === 90) return { x: p.y, y: h - p.x };
  if (r === 180) return { x: w - p.x, y: h - p.y };
  if (r === 270) return { x: w - p.y, y: p.x };
  return { x: p.x, y: p.y };
}

export function turn(r: Rotation, by: 90 | -90): Rotation {
  return ((((r + by) % 360) + 360) % 360) as Rotation;
}

/** The crop in the turned photo, the whole photo when there is none. */
export function cropOf(s: EditState, w: number, h: number): Rect {
  const size = turnedSize(w, h, s.rotation);
  return s.crop ?? { x: 0, y: 0, w: size.w, h: size.h };
}

/** A crop carried through a quarter turn, so the same part of the photo stays chosen. */
export function turnCrop(
  crop: Rect,
  w: number,
  h: number,
  from: Rotation,
  by: 90 | -90,
): Rect {
  const size = turnedSize(w, h, from);
  return by === 90
    ? { x: size.h - crop.y - crop.h, y: crop.x, w: crop.h, h: crop.w }
    : { x: crop.y, y: size.w - crop.x - crop.w, w: crop.h, h: crop.w };
}

export type Handle = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

/** A crop handle dragged by (dx, dy) turned pixels, kept inside the photo and at least `min`. */
export function dragCrop(
  crop: Rect,
  handle: Handle | "move",
  dx: number,
  dy: number,
  bounds: { w: number; h: number },
  min = 32,
): Rect {
  if (handle === "move") {
    return {
      ...crop,
      x: Math.min(Math.max(0, crop.x + dx), bounds.w - crop.w),
      y: Math.min(Math.max(0, crop.y + dy), bounds.h - crop.h),
    };
  }
  let { x, y } = crop;
  let right = crop.x + crop.w,
    bottom = crop.y + crop.h;
  if (handle.includes("w")) x = Math.min(Math.max(0, x + dx), right - min);
  if (handle.includes("e"))
    right = Math.max(Math.min(bounds.w, right + dx), x + min);
  if (handle.includes("n")) y = Math.min(Math.max(0, y + dy), bottom - min);
  if (handle.includes("s"))
    bottom = Math.max(Math.min(bounds.h, bottom + dy), y + min);
  return { x, y, w: right - x, h: bottom - y };
}

/** Scale that fits a w × h picture in a box. */
export function fitScale(
  w: number,
  h: number,
  boxW: number,
  boxH: number,
): number {
  if (w <= 0 || h <= 0 || boxW <= 0 || boxH <= 0) return 1;
  return Math.min(boxW / w, boxH / h);
}

/** Display angle of an item: its own plus the photo's turn. */
export function displayAngle(item: Placed, r: Rotation): number {
  return item.angle + r;
}

/** Whether a turned point falls inside an item (its box, turned with it), with a margin. */
export function hits(
  item: Item,
  p: Point,
  w: number,
  h: number,
  r: Rotation,
  margin = 0,
): boolean {
  const c = toTurned(item, w, h, r);
  const a = (-displayAngle(item, r) * Math.PI) / 180;
  const dx = p.x - c.x,
    dy = p.y - c.y;
  const lx = dx * Math.cos(a) - dy * Math.sin(a);
  const ly = dx * Math.sin(a) + dy * Math.cos(a);
  const halfH = Math.max(item.h / 2, item.kind === "shape" ? 6 : 0);
  return Math.abs(lx) <= item.w / 2 + margin && Math.abs(ly) <= halfH + margin;
}

/** The topmost item under a turned point. */
export function itemAt(
  items: Item[],
  p: Point,
  w: number,
  h: number,
  r: Rotation,
  margin = 0,
): Item | null {
  for (let i = items.length - 1; i >= 0; i--)
    if (hits(items[i]!, p, w, h, r, margin)) return items[i]!;
  return null;
}

// ── text ───────────────────────────────────────────────────────────────────────────────────────

/** The text's own colour and its pill: white on the pill, dark when the text itself is white. */
export function textLook(
  color: string,
  background: boolean,
): { fg: string; bg: string | null } {
  if (!background) return { fg: color, bg: null };
  return color.toLowerCase() === "#ffffff"
    ? { fg: "#ffffff", bg: INK }
    : { fg: color, bg: "#ffffff" };
}

export function fontOf(id: FontId) {
  return FONTS.find((f) => f.id === id) ?? FONTS[0];
}

/** Box of a text at a size, from measured line widths: WhatsApp's pill is 9 px wide of 17. */
export function textBox(
  lineWidths: number[],
  size: number,
): { w: number; h: number } {
  const line = size * (25 / 17);
  const padX = size * (9 / 17),
    padY = size * (1 / 17);
  const widest = Math.max(size * 0.5, ...lineWidths);
  return {
    w: widest + padX * 2,
    h: line * Math.max(1, lineWidths.length) + padY * 2,
  };
}

// ── quality ────────────────────────────────────────────────────────────────────────────────────

/** WhatsApp's standard quality: the longer side at most 1600 pixels. */
export const STANDARD_SIDE = 1600;
/** HD keeps up to WhatsApp's photo limit. */
export const HD_SIDE = 4096;
export type Quality = "standard" | "hd";

export function hasHd(w: number, h: number): boolean {
  return Math.max(w, h) > STANDARD_SIDE;
}

export function outputSize(
  w: number,
  h: number,
  quality: Quality,
): { w: number; h: number; scale: number } {
  const side = quality === "hd" ? HD_SIDE : STANDARD_SIDE;
  const scale = Math.min(1, side / Math.max(w, h));
  return {
    w: Math.max(1, Math.round(w * scale)),
    h: Math.max(1, Math.round(h * scale)),
    scale,
  };
}

let sequence = 0;
export const newItemId = () =>
  `i${Date.now().toString(36)}${(sequence++).toString(36)}`;
